import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Banknote, Calendar, Save, Loader2, Info } from 'lucide-react';
import { toast } from 'sonner';

const OfflinePaymentSettings = ({ campaignId, campaignName }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [campaign, setCampaign] = useState(null);
  const [settings, setSettings] = useState({
    allow_offline_payments: false,
    offline_payment_deadline_date: ''
  });

  useEffect(() => {
    fetchCampaign();
  }, [campaignId]);

  const fetchCampaign = async () => {
    try {
      const response = await api.get(`/campaigns/${campaignId}`);
      setCampaign(response.data);
      
      // Initialize settings from payment_config
      const paymentConfig = response.data.payment_config || {};
      setSettings({
        allow_offline_payments: paymentConfig.allow_offline_payments || false,
        offline_payment_deadline_date: paymentConfig.offline_payment_deadline_date 
          ? paymentConfig.offline_payment_deadline_date.split('T')[0] // Extract date part
          : response.data.end_date 
            ? response.data.end_date.split('T')[0] 
            : ''
      });
    } catch (error) {
      toast.error('Failed to load campaign settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Format the deadline date properly
      let deadlineDate = settings.offline_payment_deadline_date;
      if (deadlineDate && !deadlineDate.includes('T')) {
        deadlineDate = `${deadlineDate}T23:59:59Z`;
      }

      await api.put(`/campaigns/${campaignId}/offline-payment-settings`, {
        allow_offline_payments:        settings.allow_offline_payments,
        offline_payment_deadline_date: settings.allow_offline_payments ? deadlineDate : null,
      });
      
      toast.success('Offline payment settings saved successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card className="bg-gradient-to-br from-primary/5 to-secondary/5 border-primary/20 p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
            <Banknote className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-outfit font-bold text-slate-900">Offline Payments</h2>
            <p className="text-sm text-slate-600 font-jakarta mt-1">
              Allow registrants to pay in cash at the event venue. Users will receive an OTP to present at the registration desk.
            </p>
          </div>
        </div>
      </Card>

      {/* Settings Card */}
      <Card className="bg-white rounded-xl border border-slate-100 shadow-card p-6">
        <div className="space-y-6">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between py-4 border-b border-slate-100">
            <div className="flex-1">
              <Label className="text-base font-jakarta font-semibold text-slate-900">
                Enable Offline Payments
              </Label>
              <p className="text-sm text-slate-500 font-jakarta mt-1">
                When enabled, users can choose to pay in cash at the event venue
              </p>
            </div>
            <Switch
              checked={settings.allow_offline_payments}
              onCheckedChange={(checked) => setSettings(prev => ({ 
                ...prev, 
                allow_offline_payments: checked 
              }))}
            />
          </div>

          {/* Deadline Date */}
          {settings.allow_offline_payments && (
            <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
              <div>
                <Label className="text-slate-700 font-jakarta font-medium mb-2 block">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-slate-500" />
                    Offline Payment Deadline
                  </div>
                </Label>
                <Input
                  type="date"
                  value={settings.offline_payment_deadline_date}
                  onChange={(e) => setSettings(prev => ({ 
                    ...prev, 
                    offline_payment_deadline_date: e.target.value 
                  }))}
                  min={new Date().toISOString().split('T')[0]}
                  className="h-11 rounded-lg border-slate-200 focus-visible:ring-primary max-w-xs"
                />
                <p className="text-xs text-slate-500 font-jakarta mt-2">
                  OTPs will expire on this date. Users must complete payment before this deadline.
                </p>
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                <div className="flex gap-3">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-semibold mb-1">How it works:</p>
                    <ol className="list-decimal list-inside space-y-1 text-blue-700">
                      <li>User selects &quot;Pay at Venue&quot; during registration</li>
                      <li>System generates a 6-digit OTP and sends it via email</li>
                      <li>User shows OTP at the registration desk</li>
                      <li>Organizer verifies OTP and confirms cash payment</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Save Button */}
        <div className="flex justify-end mt-6 pt-6 border-t border-slate-100">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-primary hover:bg-primary-hover text-white font-jakarta"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Settings
              </>
            )}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default OfflinePaymentSettings;
